import { FULL_RANGE_END, FULL_RANGE_START } from '../config/music';
import type { ExtraValues, SynthTypeId, Waveform } from '../config/synth-types';
import { defaultExtraValues } from '../config/synth-types';
import type { EffectStates } from '../config/effects';
import { defaultEffectStates } from '../config/effects';
import type { InstrumentId } from '../config/sample-maps';
import type { PresetId } from '../config/presets';
import type { AccentLevel } from '../config/meters';

export type Engine = 'synth' | 'sampler';
export type DisplayMode = 'scroll' | 'fit1' | 'fit2';
/** Semitones between consecutive loaded samples. 1 = load every sample. */
export type SampleDensity = 1 | 3 | 6;

/**
 * The sound of one instrument (a synth type or a sampled instrument), saved so
 * each instrument keeps its own tone. Waveform/preset only apply to synths.
 */
export interface SoundProfile {
  attack: number;
  release: number;
  volume: number;
  filter: number;
  echo: number;
  reverb: number;
  waveform?: Waveform;
  preset?: PresetId | 'custom';
}

export interface Settings {
  /** Independent labels drawn on each key; any combination may be shown. */
  showLetters: boolean; // C D E …
  showSolfege: boolean; // Do Re Mi …
  showKeys: boolean; // the computer-keyboard key
  showOctave: boolean; // the octave number, drawn at the top of the key
  startAbs: number;
  endAbs: number;
  displayMode: DisplayMode;

  /** Manual keyboard height in px, or null to auto-size from the viewport. */
  keyboardHeight: number | null;

  /** Slides both row windows, in white keys. */
  rowShift: number;

  preset: PresetId | 'custom';
  engine: Engine;
  synthType: SynthTypeId;
  sampleInstrument: InstrumentId;
  sampleDensity: SampleDensity;

  waveform: Waveform;
  attack: number;
  release: number;
  volume: number;
  filter: number;
  echo: number;
  reverb: number;
  sustain: boolean;

  /** Register loudness curve, in dB per octave away from the C4 reference. */
  bassCurveDb: number;
  trebleCurveDb: number;
  /**
   * Recorded instruments already carry their own register balance — measured
   * flat within ~2.7 dB from C1 to C7 on the sampled piano — so applying the
   * curve on top of them only unbalances the instrument (it pushed C8 down by
   * 29 dB). Off by default for samples; the synth engines still need it,
   * because a raw oscillator is technically flat and therefore bass-shy.
   */
  registerCurveOnSamples: boolean;

  bpm: number;
  /** Metronome meter preset id (see config/meters.ts), or 'custom'. */
  meterId: string;
  /** Accent pattern used when meterId is 'custom'. */
  meterCustom: AccentLevel[];
  /** Per-instrument saved sound, keyed by 'synth:<type>' / 'sampler:<id>'. */
  profiles: Record<string, SoundProfile>;
  extraValues: ExtraValues;
  effects: EffectStates;
}

/**
 * Phones get a 4-octave window (C2–B5, 48 keys) by default instead of the full
 * 88 keys, which is unplayably cramped on a small screen. Evaluated per call so
 * a reset reflects the device at that moment.
 */
function defaultRange(): { startAbs: number; endAbs: number } {
  const mobile =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(max-width: 680px)').matches;
  return mobile ? { startAbs: 24, endAbs: 71 } : { startAbs: FULL_RANGE_START, endAbs: FULL_RANGE_END };
}

export const DEFAULT_SETTINGS = (): Settings => ({
  showLetters: true,
  showSolfege: false,
  showKeys: true,
  showOctave: true,
  ...defaultRange(),
  displayMode: 'fit2',

  keyboardHeight: null,

  rowShift: 0,

  preset: 'piano',
  // Boots on the real recorded piano rather than a raw oscillator; the sound
  // defaults below are the piano's dry profile (see samplerDefaultsFor in
  // controls.ts): no attack, full level and brightness, long release, no fx.
  engine: 'sampler',
  synthType: 'synth',
  sampleInstrument: 'piano',
  // Every instrument loads at the fullest sample density available.
  sampleDensity: 1,

  waveform: 'triangle',
  attack: 0,
  release: 10,
  volume: 1,
  filter: 12000,
  echo: 0,
  reverb: 0,
  sustain: true,

  bassCurveDb: 3,
  trebleCurveDb: 2,
  registerCurveOnSamples: false,

  bpm: 100,
  meterId: '4/4',
  meterCustom: [2, 0, 1, 0],
  profiles: {},
  extraValues: defaultExtraValues(),
  effects: defaultEffectStates(),
});

// Bumped to v7 with the sampler-piano defaults; old saved settings (which chose
// the synth engine) would otherwise mask the new default on existing machines.
export const STORAGE_KEY = 'webSynth.settings.v7';

export function loadSettings(): Partial<Settings> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Settings> & { notation?: string };
    // Migrate the old single `notation` enum to the three display toggles.
    if (parsed.notation !== undefined && parsed.showLetters === undefined) {
      parsed.showLetters = parsed.notation === 'letters';
      parsed.showSolfege = parsed.notation === 'solfege';
      parsed.showKeys = true;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function persist(settings: Settings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    /* storage unavailable (private browsing, quota): fail silently */
  }
}

export function clearPersisted(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Merge stored values over the defaults, keeping nested records intact. */
export function mergeSettings(base: Settings, patch: Partial<Settings> | null): Settings {
  if (!patch) return base;
  const merged: Settings = { ...base, ...patch };
  merged.extraValues = { ...base.extraValues };
  if (patch.extraValues) {
    for (const id of Object.keys(base.extraValues) as SynthTypeId[]) {
      merged.extraValues[id] = { ...base.extraValues[id], ...patch.extraValues[id] };
    }
  }
  merged.effects = { ...base.effects };
  if (patch.effects) {
    for (const key of Object.keys(base.effects)) {
      const b = base.effects[key];
      if (b) merged.effects[key] = { ...b, ...patch.effects[key] };
    }
  }
  return merged;
}
